import { client, partnerClient } from './flowit/client';
import axios from 'axios';

// Cache para evitar requisições duplicadas
let configurationCache = null;
let lastCacheTime = 0;
const CACHE_DURATION = 60000; // 60 segundos

export default {
  async getSystemConfiguration(cacheBuster = '') {
    // Se não tem cache buster e o cache ainda é válido, retornar cache
    if (!cacheBuster && configurationCache && (Date.now() - lastCacheTime) < CACHE_DURATION) {
      return configurationCache;
    }


    try {
      // Detectar se está sendo acessado por uma URL de parceiro
      const currentDomain = window.location.hostname; // hostname sem porta
      const isPartnerUrl = this.isPartnerUrl(currentDomain);

      // Força detecção de parceiro para localhost durante desenvolvimento
      const forcePartnerMode = currentDomain === 'localhost';

      // Criar client com timeout menor para configuração do sistema
      const configClient = axios.create({
        baseURL: client.defaults.baseURL,
        timeout: 8000, // 8 segundos timeout para carregamento inicial
        headers: client.defaults.headers
      });

      // Sempre usar o endpoint /api/v1/system com detecção automática de domínio
      const systemUrl = `system${cacheBuster}`;

      // Configurar headers para enviar informações de domínio
      // Nota: O browser pode bloquear o header Origin, então vamos usar alternativas
      const headers = {
        ...configClient.defaults.headers,
        'X-Forwarded-Host': currentDomain,
        'X-Panel-Domain': currentDomain,
        'X-Panel-Origin': `http://${currentDomain}:8080`
      };

      // Tentar adicionar Origin se possível, mas não falhar se for bloqueado
      try {
        headers['Origin'] = `http://${currentDomain}:8080`;
      } catch (e) {
        console.warn('⚠️ Origin header blocked by browser, using alternatives');
      }

      const response = await configClient.get(systemUrl, { headers });

      // Salvar no cache apenas se não tem cache buster
      if (!cacheBuster) {
        configurationCache = response.data;
        lastCacheTime = Date.now();
      }

      return response.data;
    } catch (error) {
      console.error('❌ Error fetching system configuration:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response && error.response.status,
        statusText: error.response && error.response.statusText,
        data: error.response && error.response.data,
        url: error.config && error.config.url,
        headers: error.config && error.config.headers
      });
      console.warn('⚠️ Using fallback configuration due to backend error');

      // Return default configuration if API fails
      // Usar ENV APP_NAME do frontend como último fallback
      const fallbackAppName = process.env.VUE_APP_APP_NAME || 'FlowIt';

      const fallbackConfig = {
        app_name: fallbackAppName,
        primary_color: '#1976D2',
        secondary_color: '#FFFFFF',
        logo_url: '/logo_flowit.png',
        logo_text_url: '/logo_flowit_text.png',
        favicon_url: '/favicon.ico',
        pwa: {
          name: fallbackAppName,
          short_name: fallbackAppName,
          description: `${fallbackAppName} - Sistema de Gestão`,
          theme_color: '#1976D2',
          background_color: '#FFFFFF'
        }
      };


      return fallbackConfig;
    }
  },

  // Converte resposta do endpoint de parceiro para o formato esperado
  convertPartnerResponseToSystemConfig(partnerData, currentUrl) {
    // Usar o nome do super partner retornado pela API, com fallback
    const appName = partnerData.app_name || this.getAppNameFromUrl(currentUrl);

    // Converter URLs relativas para absolutas apontando para o backend
    // O client.defaults.baseURL já inclui /api/v1, então usar apenas o domínio base
    const backendDomain = client.defaults.baseURL ?
      client.defaults.baseURL.replace(/\/api\/v1\/?$/, '') :
      'http://localhost:3000';

    const logoUrl = partnerData.logo_link && partnerData.logo_link.startsWith('/')
      ? `${backendDomain}${partnerData.logo_link}`
      : (partnerData.logo_link || '/logo_flowit.png');
    const faviconUrl = partnerData.favicon_link && partnerData.favicon_link.startsWith('/')
      ? `${backendDomain}${partnerData.favicon_link}`
      : (partnerData.favicon_link || '/favicon.ico');

    return {
      app_name: appName,
      primary_color: partnerData.panel_color || '#1976D2',
      secondary_color: '#FFFFFF',
      logo_url: logoUrl,
      logo_text_url: logoUrl, // Usar a mesma logo para texto por enquanto
      favicon_url: faviconUrl,
      pwa: {
        name: appName,
        short_name: this.getShortName(appName),
        description: `${appName} - Sistema de Gestão`,
        theme_color: partnerData.panel_color || '#1976D2',
        background_color: '#FFFFFF'
      }
    };
  },

  // Extrai nome da aplicação baseado na URL
  getAppNameFromUrl(url) {
    // Por agora, retorna um nome padrão, mas pode ser customizado conforme necessário
    if (url.includes('localhost:8080')) {
      return 'Partner System';
    }
    return 'FlowIt';
  },

  // Gera nome curto para PWA baseado no nome completo
  getShortName(fullName) {
    if (!fullName) return 'FlowIt';

    // Se o nome tem mais de 12 caracteres, tentar abreviar
    if (fullName.length <= 12) {
      return fullName;
    }

    // Pegar apenas o primeiro nome
    const firstName = fullName.split(' ')[0];
    if (firstName.length <= 12) {
      return firstName;
    }

    // Se ainda for muito longo, truncar
    return firstName.substring(0, 10) + '..';
  },

  // Método para detectar se a URL atual é de um parceiro
  isPartnerUrl(hostname) {
    // Detectar qualquer domínio que comece com 'painel.' ou 'panel.'
    const isPartnerPattern = hostname.startsWith('painel.') || hostname.startsWith('panel.');

    // Em desenvolvimento, sempre considerar como parceiro para testes
    const isDevelopment = hostname === 'localhost';

    return isPartnerPattern || isDevelopment;
  }
};